import { z } from 'zod';
import { ulkeKoduNormalize } from '../pazar/profil.js';
import { selectorProfileSchema } from './selector-profile.js';

/**
 * §7.2 / §9.2 — Depo tanımı ve alıcı adresleri.
 *
 * Depo bilgileri kullanıcının başka bir sisteme ait hesabıdır: şifre burada
 * saklanır ama **hiçbir zaman geri okunmaz** — API yalnızca "kayıtlı mı"
 * bilgisini döner (§23.3).
 */

export const warehouseSchema = z.object({
  name: z.string().trim().min(1, 'Depoya bir ad verin').max(80),
  baseUrl: z
    .string()
    .trim()
    .url('Geçerli bir adres girin (https://... ile başlamalı)')
    .max(300),
  formPath: z.string().trim().min(1).max(200).default('/Cargo/Form'),

  loginEmail: z.string().trim().email('Geçerli bir e-posta girin').max(200).nullable().default(null),
  /**
   * Yalnızca yazma yönünde. Boş bırakılırsa mevcut şifre korunur —
   * kullanıcı adı değiştirmek için şifresini yeniden yazmak zorunda kalmasın.
   */
  loginPassword: z.string().min(1).max(200).nullable().optional(),

  originPostcode: z.string().trim().max(20).nullable().default(null),
  isActive: z.boolean().default(true),
  isDefault: z.boolean().default(false),

  /**
   * §9.2.4 — depo sitesini yormamak için.
   *
   * Şartname 8 sorgu/dk ve 2 eşzamanlı öneriyor; bunlar **nezaket** sınırları,
   * teknik zorunluluk değil. Kullanıcının 80.000+ ürünü var ve bu tempoda çekim
   * haftalar sürerdi, bu yüzden tavanlar yükseltildi. Yine de sınırsız değil:
   * depo panelini yormak hem etik değil hem hesabın kısıtlanmasına yol açabilir.
   */
  rateLimitPerMinute: z
    .number()
    .int('Tam sayı girin')
    .min(1, 'En az 1 olmalı')
    .max(240, 'En fazla 240 olabilir')
    .default(30),
  maxConcurrency: z
    .number()
    .int('Tam sayı girin')
    .min(1, 'En az 1 olmalı')
    .max(8, 'En fazla 8 olabilir')
    .default(4),

  transitDaysDefault: z.number().int().min(0).max(120).nullable().default(null),
  processingDays: z.number().int().min(0).max(60).default(2),
  notes: z.string().max(2_000).nullable().default(null),
});
export type WarehouseInput = z.input<typeof warehouseSchema>;

/**
 * Japonya'nın 47 ili (都道府県) — hem romaji hem kanji kabul ediliyor.
 *
 * ⚠️ Neden liste var. Bu adres doğrudan depo panelinin formuna yazılıyor;
 * yanlış il, **yanlış bölge için kargo fiyatı** almak demek. Sessiz ve
 * doğrudan kâra dokunan bir hata: fiyat gelir, makul görünür, yanlıştır.
 */
export const JP_ILLERI = [
  'Hokkaido','Aomori','Iwate','Miyagi','Akita','Yamagata','Fukushima',
  'Ibaraki','Tochigi','Gunma','Saitama','Chiba','Tokyo','Kanagawa',
  'Niigata','Toyama','Ishikawa','Fukui','Yamanashi','Nagano',
  'Gifu','Shizuoka','Aichi','Mie',
  'Shiga','Kyoto','Osaka','Hyogo','Nara','Wakayama',
  'Tottori','Shimane','Okayama','Hiroshima','Yamaguchi',
  'Tokushima','Kagawa','Ehime','Kochi',
  'Fukuoka','Saga','Nagasaki','Kumamoto','Oita','Miyazaki','Kagoshima','Okinawa',
] as const;

/** `Osakafu`, `Tokyo-to`, `Ōsaka` gibi yazımları listeye eşlemek için sadeleştirir. */
function ilSadelestir(v: string): string {
  return v
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '')
    .replace(/(to|fu|ken|prefecture)$/u, '');
}

/** Girilen il adı 47 ilden birine karşılık geliyor mu? */
export function jpIlGecerli(v: string): boolean {
  const sade = ilSadelestir(v);
  return JP_ILLERI.some((il) => ilSadelestir(il) === sade);
}

/**
 * Japon posta kodunu `000-0000` biçimine getirir.
 *
 * `5770849`, `577 0849`, `〒577-0849` → `577-0849`
 * Tanıyamadığı girdiyi olduğu gibi döndürüyor; karar doğrulamaya ait.
 */
export function jpPostaKoduNormalize(v: string): string {
  const rakam = v.replace(/[^0-9]/g, '');
  return rakam.length === 7 ? `${rakam.slice(0, 3)}-${rakam.slice(3)}` : v.trim();
}

export const warehouseRecipientSchema = z.object({
  label: z.string().trim().min(1, 'Bu adrese bir etiket verin').max(60),
  fullName: z.string().trim().min(1, 'Ad soyad zorunlu').max(120),
  countryCode: z
    .string({ error: 'Ülke kodu zorunlu' })
    .trim()
    .length(2, 'İki harfli ülke kodu girin (ör. GB, MX, SA)')
    .transform(ulkeKoduNormalize),
  addressLine1: z.string().trim().min(1, 'Adres zorunlu').max(200),
  addressLine2: z.string().trim().max(200).nullable().default(null),
  city: z.string().trim().max(100).nullable().default(null),
  state: z.string().trim().max(100).nullable().default(null),
  postcode: z
    .string()
    .trim()
    .min(1, 'Posta kodu zorunlu')
    .max(20)
    .transform(jpPostaKoduNormalize),
  phone: z.string().trim().max(40).nullable().default(null),
  email: z.string().trim().email().max(200).nullable().default(null),
  isDefault: z.boolean().default(false),
  /** §9.2.5 — bölge ortalaması için ("Tokyo", "Osaka"). */
  regionLabel: z.string().trim().max(60).nullable().default(null),
})
  /*
   * ⭐ Japonya adresleri DOĞRULANIYOR.
   *
   * Bir dönem yalnızca "iki harfli ülke kodu" kontrol ediliyordu; posta kodu
   * ve il serbest metindi. Bu adres depo panelinin formuna doğrudan yazıldığı
   * için yanlış posta kodu **yanlış bölge için kargo fiyatı** almak demek —
   * fiyat gelir, makul görünür, yanlıştır. Sessiz ve doğrudan kâra dokunur.
   *
   * ⚠️ Doğrulama yalnızca `JP` için. Başka ülke kodu girilmişse (test ya da
   * ileride başka pazaryeri) kural uygulanmıyor — bilmediğimiz bir ülkenin
   * adres biçimini reddetmek, yardım etmek değil engel olmak olurdu.
   */
  .superRefine((v, ctx) => {
    if (v.countryCode !== 'JP') return;

    if (!/^[0-9]{3}-[0-9]{4}$/.test(v.postcode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['postcode'],
        message:
          'Japon posta kodu 7 haneli olmalı (ör. 577-0849). ' +
          'Boşluk, tire ya da 〒 işareti kullanabilirsiniz; biçim düzeltilir.',
      });
    }

    if (v.state !== null && v.state !== '' && !jpIlGecerli(v.state)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['state'],
        message:
          `"${v.state}" 47 Japon ilinden biri değil. ` +
          'Örnek: Tokyo, Osaka, Kanagawa. Yanlış il, yanlış bölge için kargo fiyatı demek.',
      });
    }
  });
export type WarehouseRecipientInput = z.input<typeof warehouseRecipientSchema>;

/** §9.2.3 — "Seçici Profili Düzenle" ekranı: JSON editör + "test et". */
export const updateSelectorProfileSchema = z.object({
  profile: selectorProfileSchema,
});
export type UpdateSelectorProfileInput = z.input<typeof updateSelectorProfileSchema>;

/** §20.2 `POST /warehouses/:id/test` — giriş + seçici testi. */
export const warehouseTestSchema = z.object({
  /** Denenecek ASIN. Verilmezse yalnızca giriş ve form açılışı test edilir. */
  asin: z
    .string()
    .trim()
    .regex(/^$|^(B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/i, 'Geçerli bir ASIN girin')
    .optional(),
  recipientId: z.string().optional(),
});
export type WarehouseTestInput = z.input<typeof warehouseTestSchema>;

/** `WarehouseStatus` enum'unun tamamı (§9.2.3). */
export const WAREHOUSE_STATUS_LABELS = {
  UNCONFIGURED: 'Yapılandırılmadı',
  OK: 'Çalışıyor',
  AUTH_FAILED: 'Giriş başarısız',
  SELECTOR_MISMATCH: 'Arayüz değişmiş',
  ERROR: 'Hata',
} as const;

export const WAREHOUSE_STATUS_TONE = {
  UNCONFIGURED: 'neutral',
  OK: 'success',
  AUTH_FAILED: 'danger',
  SELECTOR_MISMATCH: 'danger',
  ERROR: 'danger',
} as const satisfies Record<keyof typeof WAREHOUSE_STATUS_LABELS, string>;

/** Kullanıcı hiçbir zaman ham enum görmemeli (§19). */
export function warehouseStatusLabel(status: string): string {
  return WAREHOUSE_STATUS_LABELS[status as keyof typeof WAREHOUSE_STATUS_LABELS] ?? status;
}

/** §9.2.6 sekme 3 — toplu kargo fiyatı çekimi. */
export const cargoBatchSchema = z.object({
  /** Boş bırakılırsa kargo fiyatı olmayan tüm ürünler çekilir. */
  asins: z.array(z.string().trim().min(1)).max(100_000).optional(),
  /** Kilitli kayıtları da yeniden sor (§9.2.1 kilidini aşar). */
  force: z.boolean().default(false),
  /** Güvenlik tavanı — yanlışlıkla tüm katalog başlatılmasın. */
  limit: z.number().int().min(1).max(100_000).optional(),
});
export type CargoBatchInput = z.input<typeof cargoBatchSchema>;
